// src/pages/UserActivityDashboard.jsx

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import API from "../api";

import {
  LineChart,
  Line,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from "recharts";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";


const COLORS = ["#2F6F6A", "#B45309"];

export default function UserActivityDashboard() {
  const [streak, setStreak] = useState(null);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalTests, setTotalTests] = useState(0);
  const [performance, setPerformance] = useState(null);
  const [seriesProgress, setSeriesProgress] = useState([]);
  const [lastTests, setLastTests] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
  const { data } = await API.get("/events/dashboard");


    setStreak(data.streak);
    setTotalEvents(data.totalEvents || 0);
    setTotalTests(data.totalTests || 0);
    setPerformance(data.performance || null);
    setSeriesProgress(data.seriesProgress || []);
    setLastTests(data.lastTests || []);
    setLoading(false);
  };

  if (loading) return <p className="text-center mt-20">Loading…</p>;

  const pieData = performance
    ? [
        { name: "Passed", value: performance.passed },
        { name: "Failed", value: performance.failed }
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#FAF6EF] px-8 py-16 text-[#3A2A1A]">

      <div className="max-w-6xl mx-auto mb-12">
        <h1 className="text-4xl font-bold">📊 Learning Dashboard</h1>
        <p className="text-[#7A4A28]">Performance • Consistency • Progress</p>
      </div>

      <Grid>
        {streak && <StreakCalendar streak={streak} />}
        <Stat title="🧪 Tests" value={totalTests} />
        <Stat title="📈 Activity" value={totalEvents} />
        <Stat title="🎯 Accuracy" value={`${performance?.avgAccuracy || 0}%`} />
      </Grid>

      {performance && (
        <Grid>
          <Stat title="📊 Avg" value={`${performance.avgScore}%`} />
          <Stat title="🏆 Best" value={`${performance.bestScore}%`} />
          <Stat title="📉 Worst" value={`${performance.worstScore}%`} />
          <Stat title="🕒 Study" value={`${performance.totalStudyTime} min`} />
        </Grid>
      )}

      {/* Charts */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 mb-14">

        <Card>
          <h3 className="font-semibold mb-4">Overall Accuracy</h3>
          <CircularProgressbar
            value={performance.avgAccuracy}
            text={`${performance.avgAccuracy}%`}
            styles={buildStyles({
              pathColor: "#7A4A28",
              textColor: "#3A2A1A",
              trailColor: "#E6D8C3"
            })}
          />
        </Card>

        <Card>
          <h3 className="font-semibold mb-4">Pass / Fail</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} dataKey="value" innerRadius={50}>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <h3 className="font-semibold mb-4">Accuracy Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={lastTests}>
              <YAxis />
              <Tooltip />
              <Line dataKey="accuracy" stroke="#7A4A28" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

      </div>

      {/* Last Tests */}
      {lastTests.length > 0 && (
        <CardWide>
          <h2 className="text-xl font-bold mb-6">🧾 Last Tests</h2>

          {lastTests.map(t => (
            <div key={t.id} className="flex justify-between border-b py-3 text-sm">
              <div>
                <p className="font-semibold">{t.testName}</p>
                <p className="text-[#7A4A28]">
                  {t.score}/{t.total} — {t.accuracy}%
                </p>
              </div>
              <span className="text-[#7A4A28]">
                {new Date(t.date).toLocaleDateString()}
              </span>
            </div>
          ))}
        </CardWide>
      )}

      {/* Series */}
      {seriesProgress.length > 0 && (
        <CardWide>
          <h2 className="text-xl font-bold mb-6">📚 Test Series</h2>

          {seriesProgress.map(s => (
            <div key={s.id} className="mb-6">
              <div className="flex justify-between text-sm font-semibold">
                <span>{s.title}</span>
                <span>{s.completed}/{s.total}</span>
              </div>

              <div className="bg-[#E6D8C3] h-2 rounded mt-2">
                <div
                  className="bg-[#7A4A28] h-2 rounded"
                  style={{ width: `${s.percent}%` }}
                />
              </div>
            </div>
          ))}
        </CardWide>
      )}

    </div>
  );
}

/* UI Helpers */

function Grid({ children }) {
  return <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">{children}</div>;
}

function Card({ children }) {
  return <div className="bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-6 shadow">{children}</div>;
}

function CardWide({ children }) {
  return <div className="max-w-6xl mx-auto bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-8 shadow mb-12">{children}</div>;
}

function Stat({ title, value }) {
  return (
    <motion.div className="bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-6 shadow">
      <p className="text-sm text-[#7A4A28]">{title}</p>
      <p className="text-3xl font-bold">{value}</p>
    </motion.div>
  );
}

function StreakCalendar({ streak }) {
  const active = streak?.current || 0;
  const days = ["M","T","W","T","F","S","S"];

  return (
    <motion.div className="bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl p-6 shadow">
      <p className="text-sm text-[#7A4A28] mb-2">🔥 Streak</p>

      <div className="flex gap-2">
        {days.map((d,i)=>(
          <div key={i}
            className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-semibold
            ${i < active ? "bg-[#7A4A28] text-white" : "bg-[#E6D8C3] text-[#7A4A28]"}`}>
            {d}
          </div>
        ))}
      </div>

      <p className="text-xs mt-2 text-[#7A4A28]">{active} day streak</p>
    </motion.div>
  );
}
